import { defineStore } from "pinia";
import { markRaw, shallowRef } from "vue";
import type { PipContent } from "@/types/pip";
import { useSnackbarStore } from "@/store/snackbar.store";
import { bridgeOutsideClicks } from "@/utils/bridgeOutsideClicks";

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 800;

export const usePipStore = defineStore("pipStore", () => {
  const pipWindow = shallowRef<Window | null>(null);
  const pipContainer = shallowRef<HTMLElement | null>(null);
  const content = shallowRef<PipContent | null>(null);

  const isSupported = "documentPictureInPicture" in window;

  let disposeClicksBridge: (() => void) | null = null;

  function copyStyles(target: Window): void {
    for (const styleSheet of Array.from(document.styleSheets)) {
      try {
        const cssText = Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join("");
        const style = target.document.createElement("style");
        style.textContent = cssText;
        target.document.head.appendChild(style);
      } catch {
        if (!styleSheet.href) {
          continue;
        }
        const link = target.document.createElement("link");
        link.rel = "stylesheet";
        link.type = styleSheet.type;
        link.media = styleSheet.media.mediaText;
        link.href = styleSheet.href;
        target.document.head.appendChild(link);
      }
    }
  }

  function setContent(next: PipContent): void {
    content.value = { ...next, component: markRaw(next.component) };
  }

  async function open(content: PipContent): Promise<void> {
    const { showSnackbarError } = useSnackbarStore();

    if (!isSupported || !window.documentPictureInPicture) {
      showSnackbarError("Picture-in-Picture is not supported in this browser");

      return;
    }

    if (pipWindow.value && window.documentPictureInPicture.window === pipWindow.value) {
      pipWindow.value.focus();

      return;
    }

    try {
      const win = await window.documentPictureInPicture.requestWindow({
        width: content.width ?? DEFAULT_WIDTH,
        height: content.height ?? DEFAULT_HEIGHT,
      });

      copyStyles(win);
      win.document.body.style.margin = "0";

      const wrap = win.document.createElement("div");
      win.document.body.appendChild(wrap);

      win.addEventListener("pagehide", () => close(), { once: true });

      disposeClicksBridge = bridgeOutsideClicks(win);

      pipWindow.value = win;
      pipContainer.value = wrap;
      setContent(content);
    } catch {
      close();
    }
  }

  function close(): void {
    disposeClicksBridge?.();
    disposeClicksBridge = null;

    content.value = null;
    pipContainer.value = null;

    const win = pipWindow.value;
    pipWindow.value = null;
    win?.close();
  }

  return { isSupported, pipWindow, pipContainer, content, open, close };
});
